import { useState, useEffect, useRef } from "react";
import { FiArrowRight } from "react-icons/fi";
import { InputField, SelectField } from "components/FormFields";
import Alert from "components/common/Alert";
import { validateForm } from "utils/validation";

export default function CouponForm({
  initialData,
  onSubmit,
  alert,
  setAlert,
  eventOptions = [],
}) {
  const [formData, setFormData] = useState({
    couponName: "",
    discountPercentage: "",
    appliesTo: "all",
    selectedEvents: [],
    usageType: "once", // Default set to 'once'
    status: true,
  });

  const [errors, setErrors] = useState({});
  const alertRef = useRef(null);
  const isEdit = Boolean(initialData);

  const schema = {
    couponName: {
      required: true,
      label: "Coupon Name",
      minLength: 2,
    },
    discountPercentage: {
      required: true,
      type: "number",
      label: "Discount Percentage",
      min: 1,
      max: 100,
    },
    appliesTo: {
      required: true,
      label: "Applies To",
    },
    usageType: {
      required: true,
      label: "Usage Limit",
    },
    selectedEvents: {
      required: formData.appliesTo === "specific",
      label: "Events List",
    },
  };

  useEffect(() => {
    if (initialData) {
      let populatedEvents = [];
      if (Array.isArray(initialData.selectedEvents)) {
        populatedEvents = initialData.selectedEvents.map((item) =>
          typeof item === "object" ? item._id : item
        );
      }

      setFormData({
        couponName: initialData.couponName || "",
        discountPercentage: initialData.discountPercentage || "",
        appliesTo: initialData.appliesTo || "all",
        selectedEvents: populatedEvents,
        usageType: initialData.usageType || "once",
        status: initialData.status ?? true,
      });
    } else {
      setFormData({
        couponName: "",
        discountPercentage: "",
        appliesTo: "all",
        selectedEvents: [],
        usageType: "once",
        status: true,
      });
    }
    setErrors({});
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await validateForm(formData, schema);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    if (onSubmit) {
      onSubmit(formData);
    }
  };

  const appliesToOptions = [
    { value: "all", label: "All Events" },
    { value: "specific", label: "Specific Event(s)" },
  ];

  const usageTypeOptions = [
    { value: "once", label: "Single Use (Once)" },
    { value: "multiple", label: "Multiple Uses" },
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {alert && (
        <div ref={alertRef} className="scroll-mt-[100px] px-6 pt-3">
          <Alert type="error" message={alert} onClose={() => setAlert("")} />
        </div>
      )}

      <InputField
        label="Coupon Name / Code"
        name="couponName"
        value={formData.couponName}
        onChange={handleChange}
        placeholder="e.g. SUMMER2026"
        error={errors.couponName}
        required
      />

      <InputField
        label="Discount Percentage (%)"
        name="discountPercentage"
        type="number"
        value={formData.discountPercentage}
        onChange={handleChange}
        placeholder="e.g. 15"
        error={errors.discountPercentage}
        required
      />

      <SelectField
        label="Usage Limit"
        name="usageType"
        value={formData.usageType}
        onChange={(e) => {
          setFormData((p) => ({ ...p, usageType: e.target.value }));
          setErrors((p) => ({ ...p, usageType: "" }));
        }}
        options={usageTypeOptions}
        required
        error={errors.usageType}
      />

      <SelectField
        label="Applies To"
        name="appliesTo"
        value={formData.appliesTo}
        onChange={(e) => {
          const val = e.target.value;
          setFormData((p) => ({
            ...p,
            appliesTo: val,
            selectedEvents: val === "all" ? [] : p.selectedEvents,
          }));
          setErrors((p) => ({ ...p, appliesTo: "", selectedEvents: "" }));
        }}
        options={appliesToOptions}
        required
        error={errors.appliesTo}
      />

      {formData.appliesTo === "specific" && (
        <SelectField
          label="Select Event(s)"
          name="selectedEvents"
          value={formData.selectedEvents}
          onChange={(e) => {
            const values = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            setFormData((p) => ({ ...p, selectedEvents: values }));
            setErrors((p) => ({ ...p, selectedEvents: "" }));
          }}
          options={eventOptions}
          isMulti={true}
          isSearchable={true}
          placeholder="Search and select events..."
          required
          error={errors.selectedEvents}
        />
      )}

      <div className="mt-4 flex justify-end">
        <button type="submit" className="btn-primary">
          <span>{isEdit ? "Update" : "Save"}</span>
        </button>
      </div>
    </form>
  );
}
